/**
 * Minimal placement modal: pick trip + city day, parse date via HumanReservationDateTime.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, message } from 'antd';
import { inboundPlacementEn } from '../../content/trips.en';
import type { InboundConfirmation } from '../../services/inboundEmailService';
import { getVisibleTrips, updateTrip } from '../../services/tripsService';
import {
  applyInboundPlacementToTrip,
  cityDaysOnTrip,
  inferInboundItemType,
  matchingCityDaysForYmd,
  parseInboundReservationInstant,
  type InboundCityDayOption,
  type InboundPlacementPlan,
} from '../../utils/inboundConfirmationPlacement';
import { hmFromUnknown, ymdFromUnknown } from '../../utils/wallClock';
import styles from './InboundPlacementModal.module.css';

export interface InboundPlacementModalProps {
  open: boolean;
  confirmation: InboundConfirmation | null;
  userId: string;
  defaultTripId?: string;
  onClose: () => void;
  onImported: (confirmationId: string, tripId: string) => void;
}

function optionKey(o: InboundCityDayOption): string {
  return `${o.cityId}|${o.dateYmd}`;
}

function parseOptionKey(key: string | null): { cityId: string; dateYmd: string } | null {
  if (!key) return null;
  const idx = key.indexOf('|');
  if (idx < 0) return null;
  return { cityId: key.slice(0, idx), dateYmd: key.slice(idx + 1) };
}

const InboundPlacementModal: React.FC<InboundPlacementModalProps> = ({
  open,
  confirmation,
  userId,
  defaultTripId,
  onClose,
  onImported,
}) => {
  const trips = useMemo(() => (userId ? getVisibleTrips(userId) : []), [userId, open]);
  const [tripId, setTripId] = useState<string | null>(null);
  const [dateRaw, setDateRaw] = useState('');
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !confirmation) return;
    const suggested =
      defaultTripId ||
      confirmation.primarySuggestedTripId ||
      confirmation.importedTripId ||
      trips[0]?.id ||
      null;
    setTripId(suggested);
    setTitle(confirmation.subject?.trim() || 'Confirmation');
    setConfirmationNumber('');
    setDateRaw('');
    setDayKey(null);
    setHint(null);
  }, [open, confirmation, defaultTripId, trips]);

  const trip = trips.find((t) => t.id === tripId) ?? null;

  const cityOptions = useMemo(() => {
    if (!trip) return [];
    if (dateRaw.trim()) {
      const instant = parseInboundReservationInstant(dateRaw, trip, {
        emailHints: confirmation?.bodyPreview ?? confirmation?.subject,
        locationText: confirmation?.subject,
      });
      const ymd = instant ? ymdFromUnknown(instant) : null;
      if (ymd) {
        const matches = matchingCityDaysForYmd(trip, ymd);
        if (matches.length > 0) return matches;
      }
    }
    return cityDaysOnTrip(trip);
  }, [trip, dateRaw, confirmation]);

  useEffect(() => {
    if (!trip || !dateRaw.trim()) {
      setHint(null);
      return;
    }
    const instant = parseInboundReservationInstant(dateRaw, trip, {
      emailHints: confirmation?.bodyPreview ?? confirmation?.subject,
      locationText: confirmation?.subject,
    });
    if (!instant) {
      setHint(inboundPlacementEn.parseFailed);
      return;
    }
    const ymd = ymdFromUnknown(instant);
    if (!ymd) {
      setHint(inboundPlacementEn.parseFailed);
      return;
    }
    const matches = matchingCityDaysForYmd(trip, ymd);
    if (matches.length === 1) {
      setDayKey(optionKey(matches[0]));
      setHint(null);
    } else if (matches.length === 0) {
      setHint(inboundPlacementEn.noMatchingCity);
    } else {
      setHint(inboundPlacementEn.ambiguousCity);
    }
  }, [trip, dateRaw, confirmation]);

  const handleImport = async (): Promise<void> => {
    if (!confirmation || !userId) return;
    if (!trip) {
      message.error(inboundPlacementEn.tripRequired);
      return;
    }
    const picked = parseOptionKey(dayKey);
    if (!picked) {
      message.error(inboundPlacementEn.cityRequired);
      return;
    }

    let startHm = '12:00';
    let dateYmd = picked.dateYmd;
    if (dateRaw.trim()) {
      const instant = parseInboundReservationInstant(dateRaw, trip, {
        emailHints: confirmation.bodyPreview ?? confirmation.subject,
        locationText: confirmation.subject,
      });
      if (instant) {
        dateYmd = ymdFromUnknown(instant) ?? dateYmd;
        startHm = hmFromUnknown(instant) ?? startHm;
      } else if (!picked.dateYmd) {
        message.error(inboundPlacementEn.parseFailed);
        return;
      }
    }

    const plan: InboundPlacementPlan = {
      tripId: trip.id,
      cityId: picked.cityId,
      dateYmd,
      startHm,
      itemType: inferInboundItemType(confirmation.type),
      title: title.trim() || 'Confirmation',
      confirmationNumber: confirmationNumber.trim() || undefined,
      notes: confirmation.bodyPreview?.trim() || undefined,
    };

    setSaving(true);
    try {
      const nextTrip = applyInboundPlacementToTrip(trip, plan);
      const updated = updateTrip(userId, trip.id, {
        itineraryDays: nextTrip.itineraryDays,
        hotelStaysByCity: nextTrip.hotelStaysByCity,
        taskItems: nextTrip.taskItems,
      });
      if (!updated) {
        message.error(inboundPlacementEn.importFailed);
        return;
      }
      onImported(confirmation.id, trip.id);
      message.success(inboundPlacementEn.importSuccess);
      onClose();
    } catch {
      message.error(inboundPlacementEn.importFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={inboundPlacementEn.title}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={480}
      data-testid="inbound-placement-modal"
    >
      {trips.length === 0 ? (
        <p className={styles.muted}>{inboundPlacementEn.noTrips}</p>
      ) : (
        <div className={styles.form}>
          <label className={styles.label}>{inboundPlacementEn.trip}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={inboundPlacementEn.tripPlaceholder}
            value={tripId}
            onChange={(v) => {
              setTripId(v);
              setDayKey(null);
            }}
            options={trips.map((t) => ({
              value: t.id,
              label: t.title?.trim() || t.id,
            }))}
            showSearch
            optionFilterProp="label"
          />

          <label className={styles.label}>{inboundPlacementEn.itemTitle}</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />

          <label className={styles.label}>{inboundPlacementEn.reservationDate}</label>
          <Input
            value={dateRaw}
            onChange={(e) => setDateRaw(e.target.value)}
            placeholder={inboundPlacementEn.reservationDateHint}
            data-testid="inbound-placement-date"
          />
          {hint ? <p className={styles.hint}>{hint}</p> : null}

          <label className={styles.label}>{inboundPlacementEn.cityDay}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={inboundPlacementEn.cityDayPlaceholder}
            value={dayKey}
            onChange={setDayKey}
            options={cityOptions.map((o) => ({
              value: optionKey(o),
              label: o.label,
            }))}
            showSearch
            optionFilterProp="label"
            data-testid="inbound-placement-city"
          />

          <label className={styles.label}>{inboundPlacementEn.confirmationNumber}</label>
          <Input
            value={confirmationNumber}
            onChange={(e) => setConfirmationNumber(e.target.value)}
            placeholder={inboundPlacementEn.confirmationNumberOptional}
          />

          <div className={styles.actions}>
            <Button onClick={onClose}>{inboundPlacementEn.cancel}</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={() => void handleImport()}
              data-testid="inbound-placement-import"
            >
              {inboundPlacementEn.import}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InboundPlacementModal;
